import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Turnstile } from '@marsidev/react-turnstile'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { useAuth } from '../contexts/AuthContext'
import { CheckCircle, Eye, Users, Gift } from 'lucide-react'
import GoogleIcon from '../components/icons/GoogleIcon'

// DUB-114: Cloudflare Turnstile site key. Set in Render dashboard / .env.
// If absent in a given environment, the widget renders nothing and signup
// will fail server-side until Supabase Auth → Bot Protection is disabled or
// the key is set — log clearly so the misconfiguration is obvious.
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''

export default function Register() {
  const navigate = useNavigate()
  const { signUp, signInWithGoogle } = useAuth()
  const turnstileRef = useRef(null)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const successRef = useRef(null)

  // On mobile the "Create Account" button sits well below the success banner,
  // so after a successful signup the confirmation message was off-screen and
  // it looked like nothing happened. Scroll it into view (owner 2026-05-21).
  useEffect(() => {
    if (success) {
      successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [success])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleRegister = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const firstName = formData.firstName.trim()
    const lastName = formData.lastName.trim()
    if (!firstName || !lastName) {
      setError('Please enter your first and last name')
      setLoading(false)
      return
    }

    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setError('Please complete the verification challenge before continuing.')
      setLoading(false)
      return
    }

    try {
      await signUp(
        formData.email,
        formData.password,
        { first_name: firstName, last_name: lastName },
        { captchaToken }
      )
      setSuccess(true)
    } catch (err) {
      // Tokens are single-use — reset so the user can retry without a stale one.
      try { turnstileRef.current?.reset() } catch { /* widget not mounted */ }
      setCaptchaToken('')
      setError(err.message || 'Failed to create account')
    } finally {
      setLoading(false)
    }
  }

  const tips = [
    {
      icon: CheckCircle,
      title: "Make your picks",
      description: "Choose a tournament. Make your picks before play begins."
    },
    {
      icon: Eye,
      title: "Follow the action",
      description: "Watch the real-world event while rooting for your picks to win."
    },
    {
      icon: Users,
      title: "Compete with friends",
      description: "Create or join friendly groups and earn points to climb the leaderboard."
    },
    {
      icon: Gift,
      title: "Win cool prizes",
      description: "Top dubs get free merch!"
    }
  ]

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Welcome Section — top on mobile, left on desktop */}
      <div
        className="flex w-full lg:w-1/2 p-4 lg:p-12 items-center justify-center relative bg-cover bg-center"
        style={{ backgroundImage: 'url(/images/backgrounds/dub-action-login-2.png)' }}
      >
        {/* Gradient overlay with 20% opacity */}
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-royal-purple)] to-[var(--color-purple)] opacity-30"></div>

        <div className="bg-white/25 backdrop-blur-xl rounded-2xl p-6 lg:p-12 max-w-xl w-full border border-white/30 shadow-2xl relative z-10">
          <img src="/icons/dub-logo-white.svg" alt="dub" className="h-10 lg:h-12 mb-4 lg:mb-12" />
          <h1 className="text-3xl lg:text-5xl font-bold mb-2 lg:mb-6 text-dark-bg-heading">Go get it!</h1>
          <p className="text-base lg:text-xl text-dark-bg-muted mb-6 lg:mb-12">
            Win more than just bragging rights.
          </p>

          <div className="space-y-4 lg:space-y-6">
            {tips.map((tip, index) => (
              <div key={index} className="flex items-start gap-3 lg:gap-4">
                <div className="flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-white/10 flex items-center justify-center">
                  <tip.icon className="h-5 w-5 lg:h-6 lg:w-6 text-dark-bg" />
                </div>
                <div className="min-w-0 pt-0.5 lg:pt-0">
                  <h3 className="font-semibold text-base lg:text-lg mb-0.5 lg:mb-1 text-dark-bg-heading leading-tight">{tip.title}</h3>
                  <p className="text-sm lg:text-base text-dark-bg-muted leading-snug">{tip.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden lg:block text-sm text-dark-bg-muted mt-12 pt-8 border-t border-white/20">
            © 2026 dub. All rights reserved.
          </div>
        </div>
      </div>

      {/* Register Form — below on mobile, right on desktop */}
      <div className="w-full lg:w-1/2 flex items-start lg:items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-3xl font-bold mb-2">Create Account</h2>
          </div>

          <form onSubmit={handleRegister} className="space-y-6">
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}
            {success && (
                <div ref={successRef} className="bg-success/10 text-success text-sm p-3 rounded-md border border-[var(--color-success-border)]">
                Check your email to confirm your address before logging in. The confirmation link expires in 24 hours.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label htmlFor="firstName" className="text-sm font-medium text-muted-foreground">
                  First name
                </label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="lastName" className="text-sm font-medium text-muted-foreground">
                  Last name
                </label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="h-12"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-muted-foreground">
                Email
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-muted-foreground">
                Password
              </label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                className="h-12"
              />
            </div>
            {/* DUB-114: Cloudflare Turnstile. Renders the "managed" widget,
                which stays invisible for low-risk traffic and only shows a
                challenge when needed. Theme matches the form's light bg. */}
            {TURNSTILE_SITE_KEY && (
              <div className="flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={setCaptchaToken}
                  onError={() => setCaptchaToken('')}
                  onExpire={() => setCaptchaToken('')}
                  options={{ theme: 'light' }}
                />
              </div>
            )}

            <Button
              variant="primaryNeon"
              type="submit"
              className="w-full h-12 text-base"
              disabled={loading || (TURNSTILE_SITE_KEY ? !captchaToken : false)}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or sign up with</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-3 mt-6">
              <Button 
                variant="outline" 
                className="w-full h-12" 
                type="button"
                onClick={async () => {
                  try {
                    await signInWithGoogle()
                  } catch (err) {
                    setError(err.message || 'Failed to sign up with Google')
                  }
                }}
              >
                <GoogleIcon className="h-5 w-5 mr-2" />
                Continue with Google
              </Button>
            </div>
          </div>

          <div className="mt-8 text-center text-sm">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-primary hover:underline font-semibold"
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
